import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Team {
  team_id: number;
  team_name: string;
  team_description?: string;
  sport_type?: string;
  slug?: string;
  coach_name?: string;
}

interface UseTeamSearchOptions {
  sportFilter?: string;
}

export const useTeamSearch = (options: UseTeamSearchOptions = {}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadTeams = async () => {
      setLoading(true);
      try {
        console.log('🔍 TEAM SEARCH - Loading all teams...');
        
        // Get all teams first
        const { data: allTeamsData, error: teamsError } = await supabase
          .from('teams')
          .select(`
            team_id,
            team_name,
            team_description,
            sport_type,
            slug,
            created_by
          `)
          .order('team_name');

        if (teamsError) {
          console.error('🔍 TEAM SEARCH - Error loading teams:', teamsError);
          throw teamsError;
        }

        console.log('🔍 TEAM SEARCH - Found teams:', allTeamsData?.map(t => t.team_name));

        // For each team, get the primary coach information from team_coaches table
        const teamsWithCoaches = await Promise.all(
          (allTeamsData || []).map(async (team: any) => {
            console.log(`🔍 TEAM SEARCH - Processing team: ${team.team_name} (ID: ${team.team_id})`);
            
            let coachName = '';

            // First check if team creator is an internal user (like Bubba) - they should be shown as Primary Coach
            const creatorId = team.created_by;
            const { data: internalCreator } = await supabase
              .from('internal_users')
              .select('username')
              .eq('id', creatorId)
              .maybeSingle();

            if (internalCreator) {
              coachName = `${internalCreator.username} (Primary Coach)`;
              console.log(`🔍 TEAM SEARCH - ${team.team_name} creator from internal (primary coach):`, coachName);
            } else {
              // If creator is not internal user, check for coaches in team_coaches table
              const { data: teamCoaches } = await supabase
                .from('team_coaches')
                .select('coach_id, role')
                .eq('team_id', team.team_id)
                .limit(1);

              if (teamCoaches && teamCoaches.length > 0) {
                const coachId = teamCoaches[0].coach_id;
                
                // Try to get coach name from profiles first
                const { data: profileData } = await supabase
                  .from('profiles')
                  .select('username, first_name, last_name')
                  .eq('id', coachId)
                  .maybeSingle();

                if (profileData) {
                  coachName = profileData.username || `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim();
                  console.log(`🔍 TEAM SEARCH - ${team.team_name} coach from team_coaches (profiles):`, coachName);
                } else {
                  // Try internal users
                  const { data: internalData } = await supabase
                    .from('internal_users')
                    .select('username')
                    .eq('id', coachId)
                    .maybeSingle();

                  if (internalData) {
                    coachName = internalData.username || '';
                    console.log(`🔍 TEAM SEARCH - ${team.team_name} coach from team_coaches (internal):`, coachName);
                  }
                }
              } else {
                console.log(`🔍 TEAM SEARCH - ${team.team_name} has no assigned coaches and creator is not internal user`);
                coachName = '';
              }
            }

            const result = {
              team_id: team.team_id,
              team_name: team.team_name,
              team_description: team.team_description,
              sport_type: team.sport_type,
              slug: team.slug,
              coach_name: coachName || undefined
            };
            
            console.log(`🔍 TEAM SEARCH - ${team.team_name} final result:`, result);
            return result;
          })
        );

        console.log('🔍 TEAM SEARCH - All teams with coaches:', teamsWithCoaches.map(t => `${t.team_name} (Coach: ${t.coach_name || 'None'})`));
        
        const allTeams = teamsWithCoaches;

        setTeams(allTeams);
        setFilteredTeams(allTeams);
      } catch (error) {
        console.error('Error loading teams:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeams();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      let filtered = teams;
      
      // Apply sport filter if provided
      if (options.sportFilter) {
        filtered = teams.filter(team => 
          team.sport_type?.toLowerCase() === options.sportFilter.toLowerCase()
        );
      }
      
      setFilteredTeams(filtered);
      return;
    }

    let filtered = teams.filter(team =>
      team.team_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.slug && team.slug.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    // Apply sport filter if provided
    if (options.sportFilter) {
      filtered = filtered.filter(team => 
        team.sport_type?.toLowerCase() === options.sportFilter.toLowerCase()
      );
    }
    
    setFilteredTeams(filtered);
  }, [searchTerm, teams, options.sportFilter]);

  return {
    teams: filteredTeams,
    searchTerm,
    setSearchTerm,
    loading
  };
};